import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { BreakString } from 'foundations-components/transitional/components/BreakString';
import { ExpandableText } from 'foundations-components/transitional/components/ExpandableText';
import { Tab, Tabs } from 'foundations-components/transitional/components/TabsTransitionalOld';
import { Tag } from 'foundations-components/transitional/components/Tag';
import { useMemo, useState } from 'react';
import SanitizedText from 'sanitize-text/components/SanitizedText';
import { EcosystemListingStub } from 'trellis-ecosystem';
import { useDetailsPageContext } from '../../context/DetailsPageContext';
import { AppCard } from '../../functionality/AppCard';
import { SharedDataTable } from '../../functionality/SharedDataTable';
import { SkillsList } from '../../functionality/SkillsList';
import { EmbeddedAcademyVideo } from '../../media/EmbeddedAcademyVideo';
import { DetailsPageCard } from './DetailsPageCard';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export function ProductFunctionalityCard({
  appCards,
  academyVideoIds,
  dataSyncHowItWorks,
  dataSyncInfo,
  dataSyncs,
  knowledge,
  recommendations,
  skills
}) {
  var _tabs$2;
  const {
    productType
  } = useDetailsPageContext();
  const renderedDataSyncHowItWorks = useMemo(() => dataSyncHowItWorks && /*#__PURE__*/_jsx(BreakString, {
    children: /*#__PURE__*/_jsx(SanitizedText, {
      body: dataSyncHowItWorks
    })
  }), [dataSyncHowItWorks]);
  const renderedDataSyncInfo = useMemo(() => dataSyncInfo && /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    gap: "200",
    marginBlockStart: "600",
    children: [/*#__PURE__*/_jsx(Text, {
      typography: "heading-50",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.functionalityCard.tabs.sharedData.moreInfo"
      })
    }), /*#__PURE__*/_jsx(BreakString, {
      children: dataSyncInfo
    })]
  }), [dataSyncInfo]);
  const renderedRecommendedProducts = useMemo(() => recommendations && /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    gap: "400",
    marginBlockStart: "600",
    children: [/*#__PURE__*/_jsx(Text, {
      as: "h5",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `detailsPage.functionalityCard.tabs.recommendations.${productType}`
      })
    }), /*#__PURE__*/_jsx(Flex, {
      flexDirection: "row",
      gap: "400",
      marginBlockStart: "200",
      marginInlineStart: "200",
      flexWrap: "wrap",
      children: recommendations.map((rec, index) => /*#__PURE__*/_jsx(Flex, {
        inlineSize: "30%",
        children: /*#__PURE__*/_jsx(EcosystemListingStub, {
          imageSrc: rec.imageSrc,
          listingHref: rec.listingHref,
          name: rec.name,
          productType: rec.productType,
          providerName: rec.providerName,
          actions: rec.actions
        }, index)
      }, index))
    })]
  }), [recommendations, productType]);
  const renderedKnowledgeContent = useMemo(() => {
    return knowledge && /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      gap: "200",
      marginBlockStart: "400",
      children: [/*#__PURE__*/_jsx(Text, {
        as: "h5",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `detailsPage.functionalityCard.tabs.knowledge.name`
        })
      }), /*#__PURE__*/_jsx(Text, {
        as: "p",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `detailsPage.functionalityCard.tabs.knowledge.description`
        })
      }), /*#__PURE__*/_jsx(Flex, {
        flexDirection: "row",
        flexWrap: "wrap",
        children: knowledge.map((item, index) => /*#__PURE__*/_jsx(Tag, {
          id: item.id,
          children: item.name
        }, index))
      })]
    });
  }, [knowledge]);
  const tabs = useMemo(() => {
    const tabsArray = [];
    if (dataSyncs && dataSyncs.length > 0) {
      tabsArray.push({
        tabKey: 'sharedData',
        content: /*#__PURE__*/_jsxs(_Fragment, {
          children: [renderedDataSyncHowItWorks, /*#__PURE__*/_jsx(SharedDataTable, {
            dataSyncs: dataSyncs
          }), renderedDataSyncInfo]
        }),
        showExpand: false
      });
    }
    if (skills && skills.length > 0) {
      tabsArray.push({
        tabKey: 'skills',
        content: /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(SkillsList, {
            skills: skills
          }), renderedKnowledgeContent, renderedRecommendedProducts]
        }),
        showExpand: false
      });
    }
    if (appCards && appCards.length > 0) {
      tabsArray.push({
        tabKey: 'appCards',
        content: /*#__PURE__*/_jsx(_Fragment, {
          children: appCards.map((appCard, index) => /*#__PURE__*/_jsx(AppCard, {
            appCard: appCard,
            showBottomBorder: index !== appCards.length - 1
          }, `${appCard.name}_${index}`))
        }),
        showExpand: appCards.length > 2
      });
    }
    if (academyVideoIds && academyVideoIds.length > 0) {
      tabsArray.push({
        tabKey: 'howItWorks',
        content: /*#__PURE__*/_jsx(_Fragment, {
          children: academyVideoIds.map((id, index) => /*#__PURE__*/_jsx(EmbeddedAcademyVideo, {
            id: id
          }, index))
        }),
        showExpand: academyVideoIds.length > 2
      });
    }
    return tabsArray;
  }, [skills, appCards, dataSyncs, academyVideoIds, renderedDataSyncHowItWorks, renderedDataSyncInfo, renderedKnowledgeContent, renderedRecommendedProducts]);
  const cardTitle = useMemo(() => {
    var _tabs$;
    if (tabs.length > 1) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: `detailsPage.functionalityCard.title.${productType}`
      });
    }
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: `detailsPage.functionalityCard.tabs.${(_tabs$ = tabs[0]) === null || _tabs$ === void 0 ? void 0 : _tabs$.tabKey}.name`
    });
  }, [productType, tabs]);
  const [selectedTab, setSelectedTab] = useState((_tabs$2 = tabs[0]) === null || _tabs$2 === void 0 ? void 0 : _tabs$2.tabKey);
  if (tabs.length === 0) {
    return null;
  }
  return /*#__PURE__*/_jsx(DetailsPageCard, {
    title: cardTitle,
    children: tabs.length > 1 ? /*#__PURE__*/_jsx(Tabs, {
      selected: selectedTab,
      onSelectedChange: setSelectedTab,
      panelStyles: {
        paddingInline: '0'
      },
      children: tabs.map(tab => {
        const {
          tabKey,
          content
        } = tab;
        return /*#__PURE__*/_jsx(Tab, {
          tabId: tabKey,
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `detailsPage.functionalityCard.tabs.${tabKey}.name`
          }),
          children: tab.showExpand ? /*#__PURE__*/_jsx(ExpandableText, {
            maxHeight: 400,
            hideButtonCaret: true,
            buttonAlign: "left",
            children: content
          }) : content
        }, tabKey);
      })
    }) : /*#__PURE__*/_jsx("div", {
      children: tabs[0].content
    })
  });
}